import { gql } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { type FocusEvent, type MouseEvent } from 'react';
import { useToast } from '../../hooks/useToast';
import {
	CalendarSourceType,
	useCalendarLinks_GetCalendarLinksLazyQuery,
	useCalendarLinks_SendCalendarInviteMutation,
} from '#types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calendarLinks_getCalendarLinksQuery = gql`
	query calendarLinks_getCalendarLinks(
		$sourceType: CalendarSourceType!
		$sourceId: String!
	) {
		getCalendarLinks(sourceType: $sourceType, sourceId: $sourceId) {
			googleUrl
			outlookUrl
			webcalUrl
			downloadUrl
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const calendarLinks_sendCalendarInviteMutation = gql`
	mutation calendarLinks_sendCalendarInvite(
		$sourceType: CalendarSourceType!
		$sourceId: String!
		$recipientEmails: [String!]!
		$customizedEmail: CalendarInviteCustomizedEmailInput
	) {
		sendCalendarInvite(
			sourceType: $sourceType
			sourceId: $sourceId
			recipientEmails: $recipientEmails
			customizedEmail: $customizedEmail
		)
	}
`;

export type UseCalendarLinksInput = {
	sourceType: CalendarSourceType;
	sourceId: string;
	customerEmail?: string;
};

export const useCalendarLinks = ( {
	sourceType,
	sourceId,
	customerEmail,
}: UseCalendarLinksInput ) => {
	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>( null );
	const [ isInviteModalOpen, setIsInviteModalOpen ] = useState( false );
	const [ intro, setIntro ] = useState( '' );
	const [ recipientEmails, setRecipientEmails ] = useState<string[]>( [] );
	const { showError, showSuccess } = useToast();

	const [ getCalendarLinks, { data, loading: linksLoading } ] =
		useCalendarLinks_GetCalendarLinksLazyQuery( {
			variables: { sourceType, sourceId },
		} );

	const [ sendCalendarInviteMutation, { loading: sendLoading } ] =
		useCalendarLinks_SendCalendarInviteMutation();

	const resetModal = useCallback( () => {
		setIntro( 'Hi, \nClick below to add this event date to your calendar.' );
		setRecipientEmails( [] );
	}, [] );

	useEffect( () => {
		if ( isInviteModalOpen ) {
			resetModal();
		}
	}, [ isInviteModalOpen, resetModal ] );

	const handleRecipientEmailsChange = useCallback( ( newEmails: string[] ) => {
		setRecipientEmails( newEmails );
	}, [] );

	const handleRecipientEmailInputBlur = useCallback(
		( event: FocusEvent<HTMLInputElement> ) => {
			const email = event.target.value.trim();
			if ( !email ) {
				return;
			}
			setRecipientEmails( ( prev ) =>
				prev.includes( email ) ? prev : [ ...prev, email ]
			);
		},
		[]
	);

	const handleMenuOpen = useCallback(
		async ( event: MouseEvent<HTMLElement> ) => {
			event.stopPropagation();
			setAnchorEl( event.currentTarget );
			if ( !data ) {
				const result = await getCalendarLinks();
				if ( result.error ) {
					showError( result.error.message );
					setAnchorEl( null );
				}
			}
		},
		[
			data,
			getCalendarLinks,
			showError
		]
	);

	const handleMenuClose = useCallback( () => {
		setAnchorEl( null );
	}, [] );

	const handleInviteMenuItemClick = useCallback( () => {
		setAnchorEl( null );
		setIsInviteModalOpen( true );
	}, [] );

	const handleInviteModalClose = useCallback( () => {
		setIsInviteModalOpen( false );
	}, [] );

	const handleSendInvite = useCallback( async () => {
		try {
			const result = await sendCalendarInviteMutation( {
				variables: {
					sourceType,
					sourceId,
					recipientEmails,
					customizedEmail: { intro },
				},
			} );
			if ( result.errors?.length ) {
				result.errors.forEach( ( error ) => showError( error ) );
				return;
			}
			showSuccess( `Calendar invite sent to ${ recipientEmails.join( ', ' ) }` );
			setIsInviteModalOpen( false );
		} catch ( error ) {
			showError( error );
		}
	}, [
		sendCalendarInviteMutation,
		sourceType,
		sourceId,
		recipientEmails,
		intro,
		showError,
		showSuccess,
	] );

	const links = data?.getCalendarLinks;
	const isMenuOpen = Boolean( anchorEl ) && ( linksLoading || !!links );

	return {
		anchorEl,
		isMenuOpen,
		linksLoading,
		sendLoading,
		links,
		intro,
		setIntro,
		recipientEmails,
		handleRecipientEmailsChange,
		handleRecipientEmailInputBlur,
		isInviteModalOpen,
		handleMenuOpen,
		handleMenuClose,
		handleInviteMenuItemClick,
		handleInviteModalClose,
		handleSendInvite,
	};
};
